import { useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, History } from 'lucide-react';
import type { Booking, Deposit, DepositLedgerEntry, Persona, TopupPlan } from '@/domain/types';
import { aud, simTimeFull } from '@/utils/format';
import { studioDayKey, studioDayWithTimeOf } from '@atink/domain';
import { formatStudioDay } from '@/utils/studioDay';
import { actorLabel, currentActorId } from '@/domain/actor';
import { linkSenderRole, missedLinkOwner, type MissedLinkOwner } from '@/domain/missedLinks';
import { paybyAppointmentIso, requestPaybyExtension } from '@/domain/paybyExtensionFlow';
import { hasMoneyIn } from '@/domain/depositFlow';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';

/** Label + direction for each movement kind (mirrors depositPaidAud's signs). */
const MOVEMENT_META: Record<DepositLedgerEntry['kind'], { label: string; sign: 1 | -1 }> = {
  paid: { label: 'Paid', sign: 1 },
  topup: { label: 'Top-up', sign: 1 },
  'rollover-in': { label: 'Rollover in', sign: 1 },
  reinstate: { label: 'Reinstate', sign: 1 },
  'transfer-in': { label: 'Transfer in', sign: 1 },
  'rollover-out': { label: 'Rollover out', sign: -1 },
  forfeit: { label: 'Forfeit', sign: -1 },
  redeemed: { label: 'Redeemed', sign: -1 },
  refunded: { label: 'Refunded', sign: -1 },
  'transfer-out': { label: 'Transfer out', sign: -1 },
};

/**
 * Where a movement went, in words (bgg4 #8): the session by its date, never a
 * raw booking / deposit / piece id.
 */
export function targetLabel(e: DepositLedgerEntry, bookings: Record<string, Booking> = {}): string {
  const session = (id: string) => {
    const b = bookings[id];
    return b ? `the ${formatStudioDay(b.start, 'EEE d MMM')} session` : 'another session';
  };
  if (e.toBookingId && e.fromBookingId && e.toBookingId === e.fromBookingId) return `→ ${session(e.toBookingId)}`;
  if (e.toBookingId) return `→ ${session(e.toBookingId)}`;
  if (e.fromBookingId) return `from ${session(e.fromBookingId)}`;
  if (e.counterpartDepositId) return e.kind === 'rollover-in' ? '↔ from the earlier deposit' : '↔ to the next deposit';
  if (e.toPieceId) return '→ the piece';
  return '';
}

/**
 * The link's life as events (B4, 75ng): sent / held / resent / expired /
 * superseded / paid / placed, from the deposit and the booking it holds. The
 * money movements (ledger) sit below it. Times are the demo clock's.
 */
export function depositTimeline(
  d: Deposit,
  bookings: Record<string, Booking>,
): Array<{ key: string; at?: string; label: string }> {
  const out: Array<{ key: string; at?: string; label: string }> = [];
  const slotId = d.heldBookingId ?? d.bookingId;
  const slot = slotId ? bookings[slotId] : undefined;
  out.push({
    key: 'sent',
    at: d.createdAt,
    label: d.payAtStudio ? 'Held to pay at the studio (no link sent)' : 'Link sent',
  });
  if (slot && d.heldBookingId) out.push({ key: 'held', at: d.createdAt, label: `Slot held · ${simTimeFull(slot.start)}` });
  if (d.resentAt) out.push({ key: 'resent', at: d.resentAt, label: 'Link resent' });
  if (d.paidAt) out.push({ key: 'paid', at: d.paidAt, label: 'Paid' });
  if (d.status === 'paid' && d.bookingId && slot) out.push({ key: 'placed', label: `On the booking · ${simTimeFull(slot.start)}` });
  if (d.status === 'paid' && !d.bookingId && !d.heldBookingId) out.push({ key: 'onfile', label: `On file until ${formatStudioDay(d.expiresAt, 'd MMM yyyy')}` });
  if (d.status === 'expired') out.push({ key: 'expired', at: d.expiresAt, label: 'Link expired' });
  if (d.supersededByDepositId) out.push({ key: 'superseded', label: 'Replaced by a fresh link' });
  return out;
}

/**
 * Expandable movement history for a deposit (G2): every paid / top-up /
 * rollover / forfeit / reinstate as a linked ledger entry. Shared by the
 * deposits ledger row drawer and the customer record deposit block.
 */
export default function DepositMovementDrawer({ deposit }: { deposit: Deposit }) {
  const ledger = deposit.ledger ?? [];
  const persona = useSessionStore((s) => s.persona);
  const bookings = useBrainStore((s) => s.bookings);
  const topupPlans = useBrainStore((s) => s.topupPlans);
  return (
    <div
      data-testid={`deposit-movements-${deposit.id}`}
      className="rounded-lg border border-dashed border-gold-dim/40 bg-ink-black/40"
    >
      {canAskForMoreTime(deposit, bookings, persona, topupPlans) ? <AskForMoreTime deposit={deposit} /> : null}
      <ul className="px-3 py-2 border-b border-gold-dim/15 space-y-0.5" data-testid={`deposit-timeline-${deposit.id}`}>
        {depositTimeline(deposit, bookings).map((ev) => (
          <li key={ev.key} className="flex items-baseline justify-between gap-3 text-xs">
            <span className="font-serif text-cream-soft">{ev.label}</span>
            {ev.at ? (
              <span className="font-mono text-[10px] uppercase tracking-widest text-cream-faint">{simTimeFull(ev.at)}</span>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gold-dim/15">
        <History size={12} className="text-gold-dim" />
        <span className="label-small-caps text-cream-soft">Movement history</span>
      </div>
      {ledger.length === 0 ? (
        <p className="px-3 py-3 font-serif text-xs text-cream-soft">No movements recorded yet.</p>
      ) : (
        <ul className="divide-y divide-gold-dim/10">
          {ledger.map((e, i) => {
            const m = MOVEMENT_META[e.kind];
            const target = targetLabel(e, bookings);
            const detail = [target, e.note].filter(Boolean).join(' · ');
            return (
              <li
                key={i}
                className="grid grid-cols-[104px_84px_1fr_auto] items-center gap-3 px-3 py-2 text-xs"
              >
                <span
                  className={clsx(
                    'font-inter text-[10px] font-semibold uppercase tracking-widest',
                    m.sign < 0 ? 'text-blood' : 'text-mint',
                  )}
                >
                  {m.label}
                </span>
                <span
                  className={clsx(
                    'font-mono text-right tabular-nums',
                    m.sign < 0 ? 'text-blood' : 'text-mint',
                  )}
                >
                  {m.sign < 0 ? '−' : '+'}
                  {aud(e.amountAud)}
                </span>
                <span className="font-serif text-cream-soft truncate">
                  {detail}
                  {e.by ? <span className="text-cream-faint"> · by {actorLabel(e.by)}</span> : null}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                  {simTimeFull(e.at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// --- W5 i5: "Ask for more time to pay" on an unpaid link -------------------

/** The seats that follow up each owner's unpaid links (DECIDED 24 Sep). */
const OWNER_SEATS: Record<MissedLinkOwner, readonly Persona[]> = {
  agent: ['bookings-agent', 'studio-manager'],
  // Messenger-sent links are Ops's to follow up; the Messenger relays the ask.
  ops: ['georgina', 'messenger'],
  // The Sweeper seat (75n6) follows up its own gap-fill links.
  sweeper: ['georgina', 'sweeper'],
};

/**
 * The ask is offered on an UNPAID link, to the seat that follows it up (the
 * link's owner by who sent it: Agent / Ops for Messenger links / Sweeper).
 * Never on a part-paid deposit (Q5, DECIDED 24 Sep): that moves on the top-up
 * extension, not pay-by.
 */
export function canAskForMoreTime(
  deposit: Deposit,
  bookings: Record<string, Booking>,
  persona: Persona | null,
  topupPlans: Record<string, TopupPlan> = useBrainStore.getState().topupPlans,
): boolean {
  if (deposit.status !== 'pending' || deposit.payAtStudio || !persona) return false;
  if (hasMoneyIn(deposit, topupPlans)) return false;
  const owner = missedLinkOwner(linkSenderRole(deposit, bookings));
  return OWNER_SEATS[owner].includes(persona);
}

/** The Melbourne studio day (`YYYY-MM-DD`) for a date input, in any browser zone (88v6). */
const dateKey = (iso: string) => studioDayKey(iso);
/** A pay-by label ('14 May') on the Melbourne day, in any browser zone (88v6). */
const dayLabel = (iso: string) => formatStudioDay(iso, 'd MMM');

/** `key` moved by `n` calendar days (pure date arithmetic, no host zone). */
function addDayKeys(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/**
 * The picker's bounds (tv-2na.12.13), on Melbourne days: the latest pay-by is the
 * day before the appointment (same appointment, more days to pay), and the
 * default is min(pay-by + 2 days, that day). No resolvable appointment = no max.
 */
export function paybyPickerBounds(deposit: Deposit): { defaultKey: string; maxKey?: string; noRoom: boolean } {
  const appt = paybyAppointmentIso(deposit);
  const plusTwo = addDayKeys(dateKey(deposit.expiresAt), 2);
  if (!appt) return { defaultKey: plusTwo, noRoom: false };
  const maxKey = addDayKeys(dateKey(appt), -1);
  return {
    defaultKey: plusTwo < maxKey ? plusTwo : maxKey,
    maxKey,
    // tv-2na.12.15: the pay-by is already the day before the appointment (or
    // later), so there is no later day to pick (min >= max).
    noRoom: maxKey <= dateKey(deposit.expiresAt),
  };
}

/** The picked Melbourne day, keeping the link's cut-off time of day (88v6). */
const onDay = (baseIso: string, key: string): string => studioDayWithTimeOf(baseIso, key);

/**
 * A client wants more days to pay, same appointment (W5 i5). More than 7 days
 * out the owner extends it here and now; inside 7 days it becomes an Ops
 * request (the Pay-by extensions tab) and the link keeps its date until Ops
 * decides. The 7-day line is requestPaybyExtension's (the shared cancel split).
 */
function AskForMoreTime({ deposit }: { deposit: Deposit }) {
  const pending = useBrainStore((s) =>
    Object.values(s.paybyExtensionRequests).find((r) => r.depositId === deposit.id && r.status === 'pending'),
  );
  const pushNotification = useUIStore((s) => s.pushNotification);
  const [open, setOpen] = useState(false);
  const bounds = paybyPickerBounds(deposit);
  const [picked, setPicked] = useState(() => bounds.defaultKey);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const newPayByIso = picked ? onDay(deposit.expiresAt, picked) : '';
  const valid =
    !!picked &&
    Date.parse(newPayByIso) > Date.parse(deposit.expiresAt) &&
    (!bounds.maxKey || picked <= bounds.maxKey);

  if (pending) {
    return (
      <p
        className="px-3 py-2 border-b border-gold-dim/15 font-serif text-xs text-cream-soft"
        data-testid={`ask-time-pending-${deposit.id}`}
      >
        More time asked for: waiting on Ops to approve {dayLabel(pending.requestedPayByIso)} (inside 7 days).
      </p>
    );
  }

  const submit = () => {
    if (!valid) return;
    const res = requestPaybyExtension({
      depositId: deposit.id,
      newPayByIso,
      by: currentActorId() ?? 'system',
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    if (!res.ok) {
      setError(
        res.error === 'already-requested'
          ? 'There is already a request with Ops for this link.'
          : res.error === 'not-later'
            ? 'Pick a date after the current pay-by.'
            : res.error === 'after-appointment'
              ? 'The pay-by has to be before the appointment.'
              : res.error === 'part-paid'
                ? 'Money is already in on this deposit: use the top-up extension instead.'
            : res.error === 'not-unpaid'
              ? 'This link is no longer unpaid.'
              : "Couldn't find this link - reload and try again.",
      );
      return;
    }
    if (res.mode === 'extended') {
      pushNotification({
        intensity: 'info',
        title: 'Pay-by extended',
        body: `More than 7 days out, so it moved straight to ${dayLabel(res.newPayByIso)}. Reminder re-pointed.`,
        ttlMs: 4500,
      });
    } else {
      pushNotification({
        intensity: 'info',
        title: 'Sent to Ops for approval',
        body: `Inside 7 days of the appointment: the link keeps ${dayLabel(deposit.expiresAt)} until Ops decides.`,
        ttlMs: 4500,
      });
    }
    setOpen(false);
  };

  if (!open) {
    return (
      <div className="px-3 py-2 border-b border-gold-dim/15">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-inter uppercase tracking-widest border border-gold-dim/40 text-gold-bright hover:border-gold-bright hover:bg-gold/10 transition"
          data-testid={`ask-time-open-${deposit.id}`}
        >
          <CalendarClock size={10} /> Ask for more time to pay
        </button>
      </div>
    );
  }

  if (bounds.noRoom) {
    return (
      <div
        className="px-3 py-3 border-b border-gold-dim/15 flex flex-wrap items-start gap-3"
        data-testid={`ask-time-form-${deposit.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="flex-1 min-w-[220px] font-serif text-xs text-cream-soft" data-testid={`ask-time-no-room-${deposit.id}`}>
          The pay-by ({dayLabel(deposit.expiresAt)}) is already the day before the appointment, so there is
          no later day to give. For more time to pay, reschedule the appointment instead.
        </p>
        <button type="button" onClick={() => setOpen(false)} className="btn-quiet text-xs">
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      className="px-3 py-3 border-b border-gold-dim/15 flex flex-wrap items-end gap-3"
      data-testid={`ask-time-form-${deposit.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <label className="block">
        <span className="label-small-caps">New pay-by (now {dayLabel(deposit.expiresAt)})</span>
        <input
          type="date"
          value={picked}
          min={dateKey(deposit.expiresAt)}
          {...(bounds.maxKey ? { max: bounds.maxKey } : {})}
          onChange={(e) => setPicked(e.target.value)}
          className="mt-1 block bg-ink-black/40 border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream focus:outline-none focus:border-gold-bright"
          data-testid={`ask-time-date-${deposit.id}`}
        />
      </label>
      <label className="block flex-1 min-w-[180px]">
        <span className="label-small-caps">Why (optional)</span>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. gets paid on the 10th"
          className="mt-1 block w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-2 py-1 text-xs text-cream placeholder:text-cream-faint focus:outline-none focus:border-gold-bright"
          data-testid={`ask-time-note-${deposit.id}`}
        />
      </label>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen(false)} className="btn-quiet text-xs">
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!valid}
          className="btn-soft text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`ask-time-submit-${deposit.id}`}
        >
          Ask for more time
        </button>
      </div>
      <p className="basis-full font-serif text-[11px] text-cream-faint">
        More than 7 days before the appointment it extends straight away; inside 7 days it goes to Ops to approve.
      </p>
      {error ? (
        <p className="basis-full font-inter text-xs text-blood" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
